import { UseFormReturn } from 'react-hook-form'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form'
import { cronPresets, type JobFormData } from './schema'

interface JobFormProps {
    form: UseFormReturn<JobFormData>
    policies?: Array<{ id: number; name: string }>
    targets?: Array<{ id: number; name: string }>
}

export function JobForm({ form, policies = [], targets = [] }: JobFormProps) {
    return (
        <div className="space-y-4">
            <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                            <Input placeholder="Enter job name" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="schedule"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>Schedule (Cron Expression)</FormLabel>
                        <FormControl>
                            <Input placeholder="0 0 * * *" {...field} />
                        </FormControl>
                        <div className="text-sm text-muted-foreground">
                            <p className="mb-2">Common schedules:</p>
                            <div className="grid grid-cols-2 gap-2">
                                {cronPresets.map((preset) => (
                                    <button
                                        key={preset.value}
                                        type="button"
                                        onClick={() => field.onChange(preset.value)}
                                        className="text-left p-2 text-xs border rounded hover:bg-accent"
                                    >
                                        <div className="font-medium">{preset.label}</div>
                                        <code className="text-muted-foreground">{preset.value}</code>
                                    </button>
                                ))}
                            </div>
                        </div>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <div className="grid grid-cols-2 gap-4">
                <FormField
                    control={form.control}
                    name="policyId"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Policy</FormLabel>
                            <Select onValueChange={(value) => field.onChange(parseInt(value))} value={field.value?.toString()}>
                                <FormControl>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select policy" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    {policies.map((policy) => (
                                        <SelectItem key={policy.id} value={policy.id.toString()}>
                                            {policy.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="targetId"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Target</FormLabel>
                            <Select onValueChange={(value) => field.onChange(parseInt(value))} value={field.value?.toString()}>
                                <FormControl>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select target" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    {targets.map((target) => (
                                        <SelectItem key={target.id} value={target.id.toString()}>
                                            {target.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>

            <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>Notes (Optional)</FormLabel>
                        <FormControl>
                            <Textarea
                                placeholder="Add any notes about this job..."
                                className="resize-none"
                                rows={3}
                                {...field}
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="enabled"
                render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                            <FormLabel className="text-base">Enabled</FormLabel>
                            <div className="text-sm text-muted-foreground">
                                Enable or disable this job
                            </div>
                        </div>
                        <FormControl>
                            <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                            />
                        </FormControl>
                    </FormItem>
                )}
            />
        </div>
    )
}



